// src/plugins/translator.js
class TranslationManager {
  constructor() {
    this.translateReady = false;
    this.currentLang = 'en';
    this.listeners = [];
    this.scriptLoaded = false;
    this.initPromise = null;
  }

  init() {
    if (this.initPromise) return this.initPromise;
    if (this.scriptLoaded && this.translateReady) return Promise.resolve();

    this.initPromise = new Promise((resolve) => {
      if (document.getElementById("google-translate-script")) {
        const checkExisting = setInterval(() => {
          const select = document.querySelector(".goog-te-combo");
          if (select) {
            this.translateReady = true;
            this.scriptLoaded = true;
            clearInterval(checkExisting);
            console.log("✅ Google Translate already loaded");
            resolve();
            return;
          }
        }, 300);
        setTimeout(() => { clearInterval(checkExisting); resolve(); }, 5000);
        return;
      }

      if (!document.getElementById("google_translate_element_global")) {
        const div = document.createElement("div");
        div.id = "google_translate_element_global";
        div.style.display = "none";
        document.body.appendChild(div);
      }

      const script = document.createElement("script");
      script.id = "google-translate-script";
      script.src = "https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
      script.onerror = () => {
        console.error("❌ Failed to load Google Translate script");
        resolve();
      };
      document.body.appendChild(script);

      window.googleTranslateElementInit = () => {
        try {
          new window.google.translate.TranslateElement(
            { pageLanguage: "en", autoDisplay: false },
            "google_translate_element_global"
          );

          let attempts = 0;
          const maxAttempts = 30;
          const checkCombo = setInterval(() => {
            attempts++;
            const select = document.querySelector(".goog-te-combo");
            if (select) {
              this.translateReady = true;
              this.scriptLoaded = true;
              clearInterval(checkCombo);
              console.log("✅ Google Translate ready");
              resolve();
              this.notifyListeners();
            } else if (attempts >= maxAttempts) {
              clearInterval(checkCombo);
              console.error("❌ Google Translate combo not found");
              resolve();
            }
          }, 300);
        } catch (error) {
          console.error("❌ Error initializing Google Translate:", error);
          resolve();
        }
      };
    });

    return this.initPromise;
  }

  clearGoogleTranslateCookie() {
    document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=' + window.location.hostname;
  }

  async setLanguage(code) {
    await this.init();
    
    if (!this.translateReady) {
      console.warn("⚠️ Google Translate not ready yet");
      return false;
    }

    const previousLang = this.currentLang;
    this.currentLang = code;
    localStorage.setItem('lang', code);

    // Switching back to English - reload page for clean reset
    if (code === 'en' && previousLang !== 'en') {
      this.clearGoogleTranslateCookie();
      console.log("🔄 Reloading page to reset to English");
      window.location.reload();
      return true;
    }

    // Already on English
    if (code === 'en') {
      await this.resetTranslation();
      this.notifyListeners();
      return true;
    }

    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 30;

      const tryTranslate = setInterval(() => {
        attempts++;
        const select = document.querySelector(".goog-te-combo");

        if (select) {
          select.value = code;
          select.dispatchEvent(new Event("change", { bubbles: true }));
          clearInterval(tryTranslate);
          console.log("🌍 Language changed to:", code);
          this.notifyListeners();
          resolve(true);
        } else if (attempts >= maxAttempts) {
          clearInterval(tryTranslate);
          console.error("❌ Failed to change language");
          resolve(false);
        }
      }, 200);
    });
  }

  async resetTranslation() {
    this.clearGoogleTranslateCookie();
    
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 30;

      const tryReset = setInterval(() => {
        attempts++;
        const select = document.querySelector(".goog-te-combo");
        
        if (select) {
          select.value = '';
          select.dispatchEvent(new Event("change", { bubbles: true }));
          clearInterval(tryReset);
          console.log("🔄 Reset to English");
          setTimeout(() => resolve(true), 300);
        } else if (attempts >= maxAttempts) {
          clearInterval(tryReset);
          console.log("🔄 Forcing reload to reset");
          window.location.reload();
          resolve(false);
        }
      }, 200);
    });
  }

  getCurrentLanguage() {
    return this.currentLang;
  }

  isReady() {
    return this.translateReady;
  }

  onLanguageChange(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  notifyListeners() {
    this.listeners.forEach(callback => callback(this.currentLang));
  }

  async restoreSavedLanguage() {
    const saved = localStorage.getItem('lang');
    if (saved) {
      this.currentLang = saved;
      if (saved !== 'en') {
        await this.init();
        setTimeout(() => this.setLanguage(saved), 500);
      }
    }
  }
}

const translationManager = new TranslationManager();

export default {
  install(Vue) {
    Vue.prototype.$translator = translationManager;
  }
};

export { translationManager };